import React from 'react';
import useLocales from 'hooks/useLocales';
import { ICandidate } from 'core/interface/candidate';
import Image from 'components/image/Image';
import Pagination from 'components/shared/Pagination/Index';
import { RequestsService } from 'services/requests/request';
import { useNavigate, useParams } from 'react-router';
import { CircularProgress } from '@mui/material';
import NoData from 'components/shared/NoData/Index';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import './index.scss';
import { getDateFormatter } from 'helpers/dateConverter';
import { DynamicActionButton } from 'components/newButtons/DynamicActionButton';
import StatusIcon from 'components/newButtons/StatusIcon';

const MatchResults = ({
  handleModal,
  getMatchCandidates,
  matchProfileCandidate
}: any): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const { id, type } = useParams();

  const getUserProfile = (id: string) => {
    navigate(`/view-profile/${id}`);
  };
  const { enqueueSnackbar } = useSnackbar();
  const [candidates, setCandidates] = useState(
    matchProfileCandidate?.results || []
  );

  const sendRequest = async (candidateId: number, originId: number) => {
    try {
      const response = await RequestsService.sendRequest(
        type === 'find-candidate'
          ? { origin_profile: originId, candidate: candidateId }
          : { origin_job: originId, candidate: candidateId }
      );
      if (response && response.id) {
        setCandidates((prevCandidates: any[]) =>
          prevCandidates.map((candidate: ICandidate) =>
            candidate.id === response?.candidate?.id
              ? {
                  ...candidate,
                  profile_request: { id: response.id, status: 1 },
                  can_send_profile_request: false
                }
              : candidate
          )
        );
        enqueueSnackbar('Request has been sent', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to send request', { variant: 'error' });
    }
  };

  const withdrawRequest = async (requestId: number) => {
    try {
      const response = await RequestsService.changeRequest(
        {
          status: 4
        },
        requestId
      );
      if (response) {
        setCandidates((prevCandidates: any[]) =>
          prevCandidates.map((candidate: ICandidate) =>
            candidate.profile_request?.id === requestId
              ? {
                  ...candidate,
                  profile_request: undefined,
                  can_send_profile_request: true
                }
              : candidate
          )
        );
      }
    } catch (error) {
      enqueueSnackbar('Failed to withdraw request', { variant: 'error' });
    }
  };

  useEffect(() => {
    setCandidates(matchProfileCandidate?.results || []);
  }, [matchProfileCandidate]);

  const renderRequestButton = (candidate: ICandidate) => {
    const profileRequest = candidate.profile_request;

    if (!candidate.can_send_profile_request && profileRequest) {
      if (profileRequest.status === 1) {
        return (
          <DynamicActionButton
            label={t('Withdraw Request')}
            backgroundColor="red"
            onClick={() => withdrawRequest(profileRequest.id)}
          />
        );
      } else if (profileRequest.status === 3) {
        return <StatusIcon status="rejected" />;
      } else if (profileRequest.status === 4) {
        return <StatusIcon status="rejected" />;
      }
    }

    return (
      candidate.can_send_profile_request && (
        <DynamicActionButton
          label={t('Send request')}
          backgroundColor="#0fb398"
          onClick={() => sendRequest(+candidate.id, id ? +id : -1)}
        />
      )
    );
  };

  return (
    <>
      <div className="result-content">
        <div className="results-container">
          <p className="title">{t('Match Results')}</p>
          <div>
            {matchProfileCandidate === null ? (
              <div className="loadingContainer">
                <CircularProgress className="loading" />
              </div>
            ) : matchProfileCandidate?.results?.length === 0 ? (
              <NoData />
            ) : (
              candidates?.map((e: ICandidate) => (
                <div key={e.id} className="candidate">
                  <div className="profile-info">
                    <Image
                      className="profile-image"
                      url={e.avatar}
                      name="profile-view.svg"
                      width={75}
                      height={75}
                      objectFit="cover"
                    />

                    <div className="nameContainer">
                      <p className="name">{e.name}</p>
                      {e.bio && <span className="bio">{e.bio}</span>}
                    </div>
                  </div>
                  <div className="score">
                    <p className="score-text">
                      {e?.score}% {t('Talent Match')}
                    </p>
                    <p className="score-text">
                      {e?.culture_score}% {t('Culture Match')}
                    </p>
                  </div>
                  <div className="buttons">
                    <DynamicActionButton
                      label={t('View teaser')}
                      backgroundColor="#BEA0E5"
                      onClick={() => handleModal(true, e?.id)}
                    />

                    {e.can_view_profile && (
                      <DynamicActionButton
                        label={t('View Profile')}
                        backgroundColor="#A0D5E5"
                        onClick={() => getUserProfile(e?.id)}
                      />
                    )}
                    {renderRequestButton(e)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <Pagination
          hasNext={
            matchProfileCandidate?.next ? matchProfileCandidate.next : null
          }
          itemsPerPage={10}
          dataLength={matchProfileCandidate?.count ?? 1}
          onNext={(e) => getMatchCandidates(10, e.prevPage)}
          onPrev={(e) =>
            getMatchCandidates(10, e.prevPage !== 0 ? e.prevPage : 0)
          }
        />
      </div>
    </>
  );
};

export default MatchResults;
